import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  Validators,
  ValidatorFn,
} from '@angular/forms';

import { filter, Subscription, take, tap } from 'rxjs';

import { FloorPlanTable, FloorPlanSection } from '@shared/floor-plan/models';
import {
  ConfirmationModalComponent, ConfirmationModalResult,
  ValidationService,
} from '@tablesready/common-ui';
import { Resource } from '@shared/resource/models';
import { DEFAULT_SECTION_COLOR } from '../../../../../constants';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { AppIcon } from '@tablesready/frontend-icons';

@Component({
  selector: 'tablesready-sections',
  templateUrl: './sections.component.html',
  styleUrls: ['./sections.component.scss'],
})
export class SectionsComponent implements OnInit, OnChanges, OnDestroy {
  @Output() selectTable = new EventEmitter<string>();
  @Output() editModeChange = new EventEmitter<string | undefined>();
  @Output() selectionColorChange = new EventEmitter<string>();
  @Output() tableInputIdsChange = new EventEmitter<string[]>();

  @Input() resourcesMap: Map<string, Resource>;
  @Input() tables: FloorPlanTable[] = [];
  @Input() sections: FloorPlanSection[] = [];
  @Input() windowHeight = window.innerHeight;
  @Input() contentHeight = 100;
  @Input() disabled = false;
  @Input() onSectionClose?: ((isAddingNewSection: boolean, callback: () => void) => (name: string, tableIds: string[], color: string, sectionId?: string) => void);
  @Input() onSectionDelete?: (sectionId: string, callback: () => void) => void;

  icon = AppIcon;
  form?: FormGroup;
  editSectionId?: string;
  availableTables: FloorPlanTable[] = [];
  sectionTableCountById: Record<string, string> = {};
  selectedColor = DEFAULT_SECTION_COLOR;
  editSectionOpen = false;

  private readonly subscriptions = new Subscription();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly modalController: FrontendModalService,
    private readonly validationService: ValidationService,
  ) {}

  get tablesControl(): AbstractControl {
    return this.form.get('tables');
  }

  get nameControl(): AbstractControl {
    return this.form.get('name');
  }

  edit(section: FloorPlanSection): void {
    if (this.disabled) {
      return;
    }

    this.editSectionId = section.id;
    this.editModeChange.emit(section.id);
    this.form.get('name').setValue(section.name);
    this.selectedColor = section.color;
    this.selectedColor = section.color;
    this.editSectionOpen = true;

    this.filterAvailableTables();
    this.tableInputIdsChange.emit(section.tableIds);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  delete(): void {
    if (!this.onSectionDelete) {
      return;
    }

    this.modalController.create<ConfirmationModalComponent, ConfirmationModalResult<boolean>>(ConfirmationModalComponent, {
      options: {
        content: 'Are you sure you want to delete this section?',
        title: 'Confirm deletion',
        closeBtnText: 'Cancel',
      },
    }).pipe(
      take(1),
      filter((res) => res?.response),
      tap(() => {
        this.onSectionDelete(this.editSectionId, () => {
          this.cancelEdit();
        });
      }),
    )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.tables || changes.sections) {
      this.filterAvailableTables();
    }
  }

  tableInputItemsChange(selectedTables: FloorPlanTable[]): void {
    this.tableInputIdsChange.emit(selectedTables.map(table => table.id));
  }

  canvasTableSelectionChanges({ id, selected }: { id: string; selected: boolean }): void {
    if (!this.editSectionOpen) {
      this.selectTable.emit(id);

      return;
    }

    const selectedTables = this.tablesControl.value as string[];
    const indexInArray = selectedTables.findIndex(item => item === id);

    if (!selected && indexInArray !== -1) {
      selectedTables.splice(indexInArray, 1);
    }

    if (selected && !selectedTables.includes(id)) {
      selectedTables.push(id);
    }

    this.tablesControl.setValue(selectedTables, { emitEvent: false });
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      tables: [[]],
      name: ['', [Validators.required, this.checkSectionNameIsUnique()]],
    });

    const tablesSub = this.tablesControl.valueChanges
      .pipe(
        tap((ids: string[]) => {
          const selectedTables: FloorPlanTable[] = [];

          for (const table of this.tables) {
            if (ids.includes(table.id)) {
              selectedTables.push(table);
            }
          }

          this.tableInputItemsChange(selectedTables);
        }),
      ).subscribe();
    this.subscriptions.add(tablesSub);
  }

  addSection(): void {
    if (this.disabled) {
      return;
    }

    this.editSectionOpen = true;
    this.resetForm();
    this.editModeChange.emit('');
  }

  cancelEdit(): void {
    this.editSectionId = undefined;
    this.selectedColor = DEFAULT_SECTION_COLOR;
    this.resetForm();
    this.filterAvailableTables();
    this.editModeChange.emit(undefined);
    this.editSectionOpen = false;
  }

  resetForm(): void {
    this.tablesControl.setValue([]);
    this.nameControl.setValue('');
    this.form.markAllAsTouched();
  }

  save(): void {
    this.validationService.validateForm(this.form);
    const sectionName = this.form.get('name')?.value;

    if (!this.form.valid || !this.onSectionClose || this.validateSectionName(sectionName) !== null) {
      return;
    }

    const { name, tables } = this.form.getRawValue();
    if (this.editSectionId === undefined) {
      this.onSectionClose(true, () => {
        this.cancelEdit();
      })(name, tables, this.selectedColor);
    } else {
      this.onSectionClose(false, () => {
        this.cancelEdit();
      })(name, tables, this.selectedColor, this.editSectionId);
    }
  }

  private filterAvailableTables(): void {
    const tableMap: Record<string, [FloorPlanTable, Resource]> = {};
    const enabledTables: FloorPlanTable[] = [];
    const allUsedTableIds: string[] = [];
    this.sectionTableCountById = {};

    this.tables.forEach((table) => {
      tableMap[table.id] = [table, this.resourcesMap.get(table.resourceId)];
    });

    Object.entries(tableMap)
      .forEach(([, [table, resource]]) => {
        if (!resource?.enabled) {
          return;
        }

        enabledTables.push(table);
      });

    this.sections.forEach((section) => {
      let tableCount = 0;
      let sizeMinCount = 0;
      let sizeMaxCount = 0;

      section.tableIds.forEach((tableId) => {
        if (!tableMap[tableId]) {
          return;
        }

        const [, resource] = tableMap[tableId];
        tableCount += 1;

        sizeMinCount += resource.capacity.min;
        sizeMaxCount += resource.capacity.max;
      });
      this.sectionTableCountById[section.id] = `${tableCount} tables ${sizeMinCount}-${sizeMaxCount} guests`;

      if (section.id === this.editSectionId) {
        return;
      }

      allUsedTableIds.push(...section.tableIds);
    });

    this.availableTables = enabledTables.filter(table => !allUsedTableIds.includes(table.id));
  }

  private checkSectionNameIsUnique(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const name = control.value;
      const nameIsNotUnique = this.sections
        .findIndex(section => section.name.toLowerCase() === name.toLowerCase() && section.id !== this.editSectionId) !== -1;

      return nameIsNotUnique ? { recordAvailability: true } : null;
    }
  }

  private validateSectionName(name: string): ValidationErrors {
    let errors: ValidationErrors = null;
    if (this.sections.findIndex(section => section.name.toLowerCase() === name.toLowerCase() &&
      section.id !== this.editSectionId) !== -1) {
      errors = { recordAvailability: true };
    }

    return errors;
  }
}
